'use client';

import Link from 'next/link';
import type { Route } from 'next';
import { usePathname } from 'next/navigation';
import { BarChart3, CalendarDays, NotebookPen, Settings } from 'lucide-react';
import { RemindersMenu } from '@/components/reminders/RemindersMenu';
import { StackedMark } from '@/components/brand/StackedMark';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import { UserMenu } from './UserMenu';

interface AppRailProps {
  userName: string;
  userEmail: string;
  avatarUrl: string | null;
}

const NAV = [
  { href: '/' as Route, label: 'Schedule', icon: CalendarDays },
  { href: '/notes' as Route, label: 'Notes', icon: NotebookPen },
  { href: '/analytics' as Route, label: 'Analytics', icon: BarChart3 },
  { href: '/settings' as Route, label: 'Settings', icon: Settings },
];

export function AppRail({ userName, userEmail, avatarUrl }: AppRailProps) {
  const pathname = usePathname();

  const isActive = (href: string) =>
    href === '/'
      ? pathname === '/' || pathname.startsWith('/section')
      : pathname.startsWith(href);

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-y-0 left-0 z-40 hidden w-[68px] flex-col items-center border-r border-hairline bg-surface-sunken/60 py-5 backdrop-blur-md lg:flex"
    >
      <Link
        href="/"
        aria-label="Stacked home"
        className="group mb-8 text-foreground transition duration-fast ease-out-quint hover:scale-105"
      >
        <StackedMark size={30} />
      </Link>

      <div className="flex flex-1 flex-col items-center gap-1.5">
        {NAV.map(({ href, label, icon: Icon }) => {
          const active = isActive(href);
          return (
            <Tooltip key={href} label={label} side="right">
              <Link
                href={href}
                aria-label={label}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'relative flex h-11 w-11 items-center justify-center rounded-lg transition duration-fast ease-out-quint',
                  active
                    ? 'bg-surface-muted text-foreground shadow-sm'
                    : 'text-subtle-foreground hover:bg-surface-muted/60 hover:text-muted-foreground',
                )}
              >
                {active && (
                  <span
                    aria-hidden
                    className="absolute -left-[15px] h-5 w-[3px] rounded-full bg-accent"
                  />
                )}
                <Icon className="h-[19px] w-[19px]" strokeWidth={active ? 2.1 : 1.8} />
              </Link>
            </Tooltip>
          );
        })}
      </div>

      <div className="flex flex-col items-center gap-3">
        <RemindersMenu />
        <UserMenu name={userName} email={userEmail} avatarUrl={avatarUrl} side="right" />
      </div>
    </nav>
  );
}
